/**
 * /support/:ticketId — ticket detail + real-time chat for org members.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, LifeBuoy } from 'lucide-react';
import { watchTicket } from '@/lib/firestore';
import type { Ticket } from '@/types/models';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { TicketChat } from '@/components/tickets/TicketChat';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';

function ticketBadgeVariant(status: string) {
  if (status === 'open') return 'warning' as const;
  if (status === 'in_progress') return 'info' as const;
  return 'neutral' as const;
}

export default function SupportTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useT();

  const [ticket, setTicket] = useState<Ticket | null | undefined>(undefined);

  useEffect(() => {
    if (!ticketId) return;
    return watchTicket(ticketId, t => setTicket(t));
  }, [ticketId]);

  if (ticket === undefined) return <Loader fullscreen={false} />;
  if (!ticket) return (
    <div className="space-y-4">
      <button onClick={() => navigate('/support')} className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 dark:text-muted-foreground dark:hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />{t('support.detail.back')}
      </button>
      <p className="text-sm text-ink-500">—</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/support')}
        className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 dark:text-muted-foreground dark:hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('support.detail.back')}
      </button>

      {/* Header card */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-soft dark:border-border dark:bg-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-primary/10 dark:text-primary">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl text-ink-900 dark:text-white">{ticket.subject}</h1>
              <p className="mt-0.5 text-sm text-ink-500 dark:text-muted-foreground">
                {t(`support.category.${ticket.category}` as any)}
              </p>
            </div>
          </div>
          <Badge variant={ticketBadgeVariant(ticket.status)}>
            {t(`support.status.${ticket.status}` as any)}
          </Badge>
        </div>

        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-500 dark:text-muted-foreground">
          <div className="flex gap-1">
            <dt className="font-medium">{t('support.detail.created_by')}:</dt>
            <dd>{ticket.createdByName || ticket.createdByEmail}</dd>
          </div>
          <div className="flex gap-1">
            <dt className="font-medium">{t('support.detail.opened')}:</dt>
            <dd>{fmtDate(ticket.createdAt, locale)}</dd>
          </div>
        </dl>
      </div>

      {/* Chat */}
      <TicketChat ticketId={ticket.id} status={ticket.status} />
    </div>
  );
}
